import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import { Navigate } from 'react-router-dom';
import Toast from 'react-bootstrap/Toast';
import ToastContainer from 'react-bootstrap/ToastContainer';

import axios from 'axios';
import { setToken } from './authSlice';
export const apiURL=process.env.REACT_APP_API_BASE_URL || 'http://localhost:11080';

export function isTokenValid(token) {
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    if (!decoded?.exp) return false;
    const now = Date.now() / 1000;
    return decoded.exp > now;
  } catch (e) {
    return false; // malformed token
  }
}

export function ShowIfLoggedIn({ children }) {
  const dispatch = useDispatch();

  const token = useSelector(state => state.auth?.token);
  const refreshToken = useSelector(state => state.auth?.refreshToken);
  const user = useSelector(state => state.auth?.user);

  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
      const validateSession = async () => {
      if (!token || !user) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      // token still valid
      if (isTokenValid(token)) {
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }

      // proactively refresh if refresh token exists
      if (refreshToken) {
        try {
          const res = await axios.post(apiURL +'/api/auth/refresh', {}, {
              headers: { Authorization: `Bearer ${refreshToken}` }
          });
          dispatch(setToken(res.data.access_token));
          setIsAuthenticated(true);
        } catch (err) {
          // refresh failed → logout
          console.log('### Session expired');
        }
      } else {
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };

    validateSession();
  }, [token, refreshToken, user]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 p-3">
        <div className="text-center">
          <div className="spinner-border mb-3" />
          <div>Validating session...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children;

}

export function ShowIfLoggedInAndVerified({ children }) {
  const dispatch = useDispatch();

  const token = useSelector(state => state.auth?.token);
  const refreshToken = useSelector(state => state.auth?.refreshToken);
  const user = useSelector(state => state.auth?.user);

  const isEmailVerified = user?.email_verified;
  const mustChangePassword = user?.must_change_password;
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [emailAndPasswordChangeRequired, setEmailAndPasswordChangeRequired] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
      const validateSession = async () => {
      if (!token || !user) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }
      if (!isEmailVerified || mustChangePassword) {
        setIsAuthenticated(false);
        setEmailAndPasswordChangeRequired(true);
        setIsLoading(false);
        return ; // render nothing
      }
      // token still valid
      if (isTokenValid(token)) {
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }

      // proactively refresh if refresh token exists
      if (refreshToken) {
        try {
          const res = await axios.post(apiURL +'/api/auth/refresh', {}, {
              headers: { Authorization: `Bearer ${refreshToken}` }
          });
          dispatch(setToken(res.data.access_token));
          setIsAuthenticated(true);
        } catch (err) {
          // refresh failed → logout
          console.log('### Session expired');
        }
      } else {
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };

    validateSession();
  }, [token, refreshToken, user, isEmailVerified, mustChangePassword]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 p-3">
        <div className="text-center">
          <div className="spinner-border mb-3" />
          <div>Validating session...</div>
        </div>
      </div>
    );
  }

  if (emailAndPasswordChangeRequired) {
    return (
        <ToastContainer position="top-end" className="p-3">
            <Toast onClose={() => setShow(false)} show={show} delay={5000} autohide>
            <Toast.Header>
                <strong className="me-auto">Notification</strong>
                <small>Email Verification or Change Password</small>
            </Toast.Header>
            <Toast.Body>One of the Email Verification or Password change not done</Toast.Body>
            </Toast>
        </ToastContainer>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

export function ShowIfLoggedInAndNotVerified({ children }) {
  const user = useSelector(state => state.auth?.user);

  const isEmailVerified = user?.email_verified;

  if (!children) return null;
  if (ShowIfLoggedIn({ children })) {
    if (!isEmailVerified) {
      return children; 
    }
  }

  return null ;
}

export function ShowIfLoggedInMustChangePwd({ children }) {
  const user = useSelector(state => state.auth?.user);

  const mustChangePassword = user?.must_change_password;

  if (!children) return null;
  if (ShowIfLoggedIn({ children })) {
    if (mustChangePassword) {
      return children; 
    }
  }
  return null;
}

export function ShowIfNotLoggedIn({ children }) {
  if (!children) return null;
  if (ShowIfLoggedIn({ children })) {
    return null;
  }
  return children;;
}